import { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, Save, X } from 'lucide-react';

interface SaveCalculationDialogProps<T = unknown> {
  open: boolean;
  onClose: () => void;
  data: T;
  onSave: (name: string, data: T, description?: string) => Promise<void>;
  defaultName?: string;
  toolType?: string;
}

export function SaveCalculationDialog<T extends { projectName?: string }>({
  open,
  onClose,
  data,
  onSave,
  defaultName = '',
  toolType = 'calculation',
}: SaveCalculationDialogProps<T>) {
  const [projectName, setProjectName] = useState(defaultName);
  const [inputDate, setInputDate] = useState('');
  const [description, setDescription] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasPrefilled = useRef(false);
  const isFatigue = toolType === 'fatigue';

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      // Reset prefill flag on each open
      hasPrefilled.current = false;
      setDescription('');
      setError(null);
      if (isFatigue) {
        setProjectName('');
        setInputDate('');
        return;
      }
      // Only reset projectName if defaultName provided, otherwise let prefill handle it
      if (defaultName) {
        setProjectName(defaultName);
        hasPrefilled.current = true;
      }
    }
  }, [open, defaultName, isFatigue]);

  // Pre-fill project name from data (runs after reset effect)
  useEffect(() => {
    if (open && !hasPrefilled.current) {
      if (isFatigue) {
        const meta = (data as { meta?: { person_name?: string; input_date?: string } })?.meta;
        const duties = (data as { duties?: Array<{ day?: string }> })?.duties;
        const personName = meta?.person_name;
        const metaDate = meta?.input_date;
        const firstDutyDate = Array.isArray(duties)
          ? duties.find((duty) => typeof duty.day === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(duty.day))?.day
          : undefined;
        const today = new Date().toISOString().slice(0, 10);

        if (personName) setProjectName(personName);
        if (metaDate) {
          setInputDate(metaDate);
        } else if (firstDutyDate) {
          setInputDate(firstDutyDate);
        } else {
          setInputDate(today);
        }
        hasPrefilled.current = true;
        return;
      }

      // Check root level (EVM, Target Cost) and nested (CVR)
      const rootName = (data as { projectName?: string })?.projectName;
      const nestedName = (data as { details?: { projectName?: string } })?.details?.projectName;
      const foundName = rootName || nestedName;
      if (foundName) {
        setProjectName(foundName);
      }
      hasPrefilled.current = true; // Mark as attempted regardless of success
    }
  }, [open, data, isFatigue]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!projectName.trim()) {
      setError(isFatigue ? 'Person name is required' : 'Project name is required');
      return;
    }
    if (isFatigue && !inputDate.trim()) {
      setError('Input date is required');
      return;
    }
    
    setIsSaving(true);
    setError(null);
    
    try {
      if (isFatigue) {
        const baseData = (data && typeof data === 'object') ? data : {};
        const meta = (baseData as { meta?: Record<string, unknown> })?.meta ?? {};
        const payload = {
          ...(baseData as Record<string, unknown>),
          meta: {
            ...meta,
            person_name: projectName.trim(),
            input_date: inputDate.trim(),
            version: typeof meta?.version === 'number' ? meta.version : 1,
          },
        };
        const displayName = `${projectName.trim()} - ${inputDate.trim()}`;
        await onSave(displayName, payload as T, description.trim() || undefined);
      } else {
        await onSave(projectName.trim(), data, description.trim() || undefined);
      }
      onClose();
    } catch (error) {
      console.error('Error saving calculation:', error);
      setError(error instanceof Error ? error.message : 'Failed to save calculation');
    } finally {
      setIsSaving(false);
    }
  };

  const nameLabel = isFatigue ? 'Person Name' : 'Project Name';
  const namePlaceholder = isFatigue ? 'Enter person name' : 'Enter project name';
  const toolTitle = isFatigue ? 'Schedule' : `${toolType.charAt(0).toUpperCase()}${toolType.slice(1)}`;
  const toolPlural = isFatigue ? 'schedules' : `${toolType}s`;
  const isSaveDisabled = isSaving || !projectName.trim() || (isFatigue && !inputDate.trim());
  const showNameError = Boolean(error && error.toLowerCase().includes('name'));
  const showDateError = Boolean(error && error.toLowerCase().includes('date'));

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isSaving && !isOpen && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <Save className="h-5 w-5 mr-2 text-blue-500" />
            Save {toolTitle}
          </DialogTitle>
          <DialogDescription>
            Save your current work to access it later
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSave} className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="projectName" className="text-sm font-medium">
              {nameLabel} <span className="text-red-500">*</span>
            </Label>
            <Input
              id="projectName"
              value={projectName}
              onChange={(e) => {
                setProjectName(e.target.value);
                setError(null);
              }}
              placeholder={namePlaceholder}
              disabled={isSaving}
              className={showNameError ? "border-red-300 focus:border-red-500" : ""}
              required
              autoFocus
            />
            {showNameError && <p className="text-red-500 text-xs">{error}</p>}
          </div>

          {isFatigue && (
            <div className="space-y-2">
              <Label htmlFor="inputDate" className="text-sm font-medium">
                Input Date <span className="text-red-500">*</span>
              </Label>
              <Input
                id="inputDate"
                type="date"
                value={inputDate}
                onChange={(e) => {
                  setInputDate(e.target.value);
                  setError(null);
                }}
                disabled={isSaving}
                className={showDateError ? "border-red-300 focus:border-red-500" : ""}
                required
              />
              {showDateError && <p className="text-red-500 text-xs">{error}</p>}
            </div>
          )}
          
          <div className="space-y-2">
            <Label htmlFor="description" className="text-sm font-medium">
              Description <span className="text-gray-400 text-xs">(optional)</span>
            </Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Add a brief description..."
              disabled={isSaving}
              rows={3}
            />
          </div>
          
          <div className="bg-gray-50 -mx-6 px-6 py-3 border-t border-gray-100">
            <p className="text-xs text-gray-500 mb-2">
              This will save your current work and make it available in your saved {toolPlural} list
            </p>
          </div>
          
          <DialogFooter className="sm:justify-between">
            <div className="flex gap-2 w-full justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={isSaving}
                className="flex items-center"
              >
                <X className="h-4 w-4 mr-1" />
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={isSaveDisabled} 
                className="flex items-center"
              >
                {isSaving ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Save className="mr-2 h-4 w-4" />
                )}
                {isSaving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
